import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { getValueByPath, formatValue as metaFormatValue } from '../Meta/metaUtils';

export default function DonutChart({ rows = [], xKey, yAxisKeys = [], theme = {}, title, onExposeLegend, dataContext: incomingDataContext, onDataPointClick }) {
    const chartHeight = theme?.height ?? theme?.chartHeight ?? '100%';
    const chartWidth = theme?.width ?? theme?.chartWidth ?? '100%';

    const keys = Array.isArray(yAxisKeys) ? yAxisKeys : [];
    const valueMode = theme?.valueMode || 'sum'; // default to sum for donut breakdowns

    const segments = useMemo(() => {
        const themeSegs = Array.isArray(theme?.segments) ? theme.segments : [];

        // 1) Explicit theme-driven segments
        if (themeSegs.length > 0) {
            return themeSegs.map(s => ({
                label: String(s.label ?? ''),
                value: Number(s.value) || 0,
                color: s.color
            }));
        }

        // 2) Multiple y-keys: treat each key as a segment aggregated across rows
        const computeVal = (key) => {
            if (!Array.isArray(rows) || rows.length === 0) return 0;
            if (valueMode === 'sum') return rows.reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
            if (valueMode === 'avg') {
                const t = rows.reduce((a, r) => a + (Number(r?.[key]) || 0), 0);
                return rows.length ? t / rows.length : 0;
            }
            const last = rows[rows.length - 1];
            return Number(last?.[key]) || 0;
        };

        if (keys.length > 1) {
            return keys.map((k, i) => ({
                label: String(theme?.labels?.[i] ?? k),
                value: computeVal(k),
                color: theme?.colors?.[i]
            }));
        }

        // 3) Single y-key with rows: build segments from xKey categories
        const yKey = keys[0];
        if (yKey && Array.isArray(rows) && rows.length > 0 && xKey) {
            return rows.map((r, i) => ({
                label: String(r?.[xKey] ?? `Item ${i + 1}`),
                value: Number(r?.[yKey]) || 0,
                color: undefined
            }));
        }

        return [];
    }, [rows, xKey, keys, valueMode, theme?.segments, theme?.colors, theme?.labels]);

    const labels = useMemo(() => segments.map(s => s.label), [segments]);
    const series = useMemo(() => segments.map(s => s.value), [segments]);
    const colors = useMemo(() => {
        const segColors = segments.map(s => s.color).filter(Boolean);
        if (segColors.length) return segColors;
        if (Array.isArray(theme?.colors) && theme.colors.length) return theme.colors;
        return undefined;
    }, [segments, theme?.colors]);

    // Build a lightweight data context for path resolution similar to meta components
    const dataContext = useMemo(() => ({
        ...(incomingDataContext || {}),
        rows,
        normalized: { ...(incomingDataContext?.normalized || {}), xAxisKey: xKey, yAxisKeys }
    }), [rows, xKey, yAxisKeys, incomingDataContext]);

    // Helpers to interpret center config using path/value/default with formatting
    const resolveCenterField = (cfg, fallbackValue, defaultFormat) => {
        const normalizedCfg = (typeof cfg === 'string' || typeof cfg === 'number') ? { default: cfg } : (cfg || {});
        const raw = typeof normalizedCfg.path === 'string' ? getValueByPath(dataContext, normalizedCfg.path) : undefined;
        const effective = (raw !== undefined && raw !== null) ? raw : (normalizedCfg.hasOwnProperty('value') ? normalizedCfg.value : (normalizedCfg.hasOwnProperty('default') ? normalizedCfg.default : fallbackValue));
        const display = metaFormatValue(effective, normalizedCfg.format || defaultFormat, {
            prefix: normalizedCfg.prefix || '',
            suffix: normalizedCfg.suffix || '',
            maximumFractionDigits: normalizedCfg.maximumFractionDigits ?? 0,
        });
        const isVisible = normalizedCfg?.visible === true; // default false
        return { display, isVisible, cfg: normalizedCfg };
    };

    const seriesTotal = useMemo(() => (Array.isArray(series) ? series.reduce((a, b) => a + (Number(b) || 0), 0) : 0), [series]);

    const center = theme?.center || {};
    const titleResolved = resolveCenterField(center.title, seriesTotal, 'number');
    const subtitleResolved = resolveCenterField(center.subtitle, 'Total', 'string');

    const sizeToPx = (v, fallback) => {
        if (typeof v === 'number') return `${v}px`;
        if (typeof v === 'string') {
            const map = { xs: '12px', sm: '14px', md: '16px', lg: '20px', xl: "24px", xl2: '28px', '2xl': '32px', '3xl': '36px', '4xl': '40px' };
            if (map[v]) return map[v];
            if (v.endsWith('px')) return v;
        }
        return fallback || '16px';
    };
    const weightToNum = (v, fallback) => {
        if (typeof v === 'number') return v;
        const map = { thin: 100, extralight: 200, light: 300, normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900 };
        return map[String(v || '')?.toLowerCase()] || (fallback || 400);
    };

    const options = useMemo(() => ({
        chart: { type: 'donut', sparkline: { enabled: !(center && (titleResolved.isVisible || subtitleResolved.isVisible)) } },
        labels,
        colors,
        plotOptions: {
            pie: {
                startAngle: typeof theme?.startAngle === 'number' ? theme.startAngle : -90,
                endAngle: typeof theme?.endAngle === 'number' ? theme.endAngle : 90,
                donut: {
                    size: theme?.hollowSize || '65%',
                    // NOTE: Static demo block (kept for reference):
                    // labels: {
                    //     show: true,
                    //     name: { fontSize: '1.2rem' },
                    //     value: { fontSize: '1.2rem' },
                    //     total: { show: true, showAlways: true, fontSize: '1.2rem', label: 'Total', formatter: () => '31%' }
                    // }

                    // Dynamic, path-driven center labels (active):
                    labels: {
                        show: false,
                        // show: Boolean(center && (titleResolved.isVisible || subtitleResolved.isVisible)),
                        name: {
                            show: false,
                            // show: titleResolved?.isVisible,
                            // color: center?.title?.color || '#ffff00',
                            // fontSize: sizeToPx(center?.title?.size, '16px'),
                            // fontWeight: center?.title?.fontWeight || 400,
                        },
                        value: {
                            show: false,
                            // show: subtitleResolved.isVisible,
                            // color: center?.subtitle?.color || '#0000ff',
                            // fontSize: sizeToPx(center?.subtitle?.size, '16px'),
                            // fontWeight: center?.subtitle?.fontWeight || 400,
                        },
                        total: {
                            show: false,
                            // showAlways: true,
                            // show: Boolean(titleResolved.isVisible || subtitleResolved.isVisible),
                            // label: subtitleResolved?.isVisible ? subtitleResolved.display : '',
                            // formatter: () => titleResolved.display,
                            // fontSize: sizeToPx(center?.title?.size, '28px'),
                            // fontWeight: center?.title?.fontWeight || 700,
                            // color: center?.title?.color || '#111827'
                        }
                    }
                }
            }
        },
        legend: {
            show: theme?.legend === false ? false : (theme?.legend === true ? true : (typeof theme?.legend?.show === 'boolean' ? theme.legend.show : Boolean(theme?.legend))),
            position: typeof theme?.legend?.position === 'string' ? theme.legend.position : 'bottom',
            horizontalAlign: typeof theme?.legend?.horizontalAlign === 'string' ? theme.legend.horizontalAlign : 'center'
        },
        dataLabels: { enabled: Boolean((theme?.showLabels ?? theme?.dataLabels) === true) },
        tooltip: { enabled: Boolean(theme?.tooltip !== false) },
        events: {
            dataPointSelection: (_event, _chartContext, config) => {
                const idx = config?.dataPointIndex;
                if (typeof idx === 'number' && typeof onDataPointClick === 'function') {
                    const seg = segments?.[idx];
                    const payload = {
                        index: idx,
                        label: seg?.label,
                        value: seg?.value,
                        type: 'donut',
                    };
                    try { onDataPointClick(payload); } catch (_e) {}
                }
            }
        }
    }), [labels, colors, theme?.startAngle, theme?.endAngle, theme?.hollowSize, theme?.legend, theme?.showLabels, theme?.dataLabels, theme?.tooltip, center, titleResolved.display, titleResolved.isVisible, subtitleResolved.display, subtitleResolved.isVisible, segments, onDataPointClick]);

    // Expose legend items for external consumers after render commit
    const exposedItems = useMemo(() => (segments || []).map((s, i) => ({ label: s.label, color: (colors && colors[i]) || s.color })), [segments, colors]);

    React.useEffect(() => {
        if (typeof onExposeLegend === 'function') {
            onExposeLegend(exposedItems);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(exposedItems)]);

    return (
        <div style={{ height: '280px', width: '100%' }} className={`relative flex flex-1 justify-center items-center`}>
            <Chart
                type="donut"
                height={chartHeight}
                width={chartWidth}
                series={series}
                options={options}
            />

            {(titleResolved.isVisible || subtitleResolved.isVisible) ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
                        {titleResolved.isVisible ? (
                            <div style={{
                                color: center?.title?.color || '#111827',
                                fontSize: sizeToPx(center?.title?.size, '24px'),
                                fontWeight: weightToNum(center?.title?.fontWeight, 600),
                                lineHeight: 1
                            }}>
                                {titleResolved.display}
                            </div>
                        ) : null}
                        {subtitleResolved.isVisible ? (
                            <div style={{
                                color: center?.subtitle?.color || '#9ca3af',
                                fontSize: sizeToPx(center?.subtitle?.size, '14px'),
                                fontWeight: weightToNum(center?.subtitle?.fontWeight, 500)
                            }}>
                                {subtitleResolved.display}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}


